import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Row } from 'react-bootstrap';
import DynamicContactField from './DynamicContactField';
import { validateEmail, validatePhoneNumber } from '../../../utils/validations';
import messages from '../messages';
import {
  MAX_EMAIL_FIELDS,
  MAX_PHONE_FIELDS,
  EMAIL_TYPES,
  PHONE_TYPES,
  DEFAULT_EMAIL_ENTRY,
  DEFAULT_PHONE_ENTRY,
} from './constants';

const AddContactButton = ({ onClick, message }) => (
  <a className="btn-text" onClick={onClick} style={{ cursor: 'pointer' }}>
    <FormattedMessage {...message} />
  </a>
);

const ContactFieldArray = ({
  fields,
  type, // 'email' or 'phone'
  disabled,
  intl,
  layout = 'horizontal',
  required = false,
  colSizes,
  change,
  form,
}) => {
  const isEmail = type === 'email';
  const maxFields = isEmail ? MAX_EMAIL_FIELDS : MAX_PHONE_FIELDS;
  const typeOptions = isEmail ? EMAIL_TYPES : PHONE_TYPES;
  const defaultEntry = isEmail ? DEFAULT_EMAIL_ENTRY : DEFAULT_PHONE_ENTRY;
  const addMessage = isEmail ? messages.addEmail : messages.addPhone;

  // Generate placeholder text based on field index
  const getPlaceholder = (index) => {
    const ordinals = ['Primary', 'Secondary', 'Tertiary'];
    const ordinal = ordinals[index] || `${index + 1}`;
    const fieldType = isEmail ? 'Email' : 'Phone';
    return `${ordinal} ${fieldType}`;
  };

  // Internationalize type options
  const localizedTypeOptions = useMemo(() => {
    return typeOptions.map((option) => {
      const messageKey = isEmail
        ? `emailType${option.text}`
        : `phoneType${option.text}`;
      const text = messages[messageKey]
        ? intl.formatMessage(messages[messageKey])
        : option.text;
      return { ...option, text };
    });
  }, [isEmail, intl, typeOptions]);

  // Memoized validation functions to prevent re-renders
  const validateRequired = useMemo(() => {
    return (value) => {
      if (required && !value) {
        return intl.formatMessage(messages.required);
      }
      return false;
    };
  }, [intl, required]);

  const validateContact = useMemo(() => {
    return (value) => {
      if (!value) return false;

      const isValid = isEmail
        ? validateEmail(value)
        : validatePhoneNumber(value);
      if (!isValid) {
        return intl.formatMessage(
          isEmail ? messages.invalidEmail : messages.invalidPhoneNumber,
        );
      }
      return false;
    };
  }, [isEmail, intl]);

  const validateDuplicates = useMemo(() => {
    return (value, allValues, props, name) => {
      if (!value) return false;

      // Extract field array name from current field name
      const fieldArrayPath = name.substring(0, name.lastIndexOf('['));
      const currentFieldArray = fieldArrayPath
        .split('.')
        .reduce((obj, key) => obj?.[key], allValues);

      if (!currentFieldArray || !Array.isArray(currentFieldArray)) {
        return false;
      }

      const fieldKey = isEmail ? 'emailAddress' : 'phoneNumber';
      const duplicateCount = currentFieldArray.filter(
        (item) => item?.[fieldKey] === value,
      ).length;

      if (duplicateCount > 1) {
        return intl.formatMessage(
          isEmail
            ? messages.emailAddressesMustBeDifferent
            : messages.phoneNumbersMustBeDifferent,
        );
      }
      return false;
    };
  }, [isEmail, intl]);

  // Combine all validations
  const validate = useCallback(
    (value, allValues, props, name) => {
      // Only validate primary field as required
      const fieldIndex = parseInt(name.match(/\[(\d+)\]/)?.[1] || '0');
      const isFirstField = fieldIndex === 0;

      if (isFirstField) {
        const requiredError = validateRequired(value);
        if (requiredError) return requiredError;
      }

      const contactError = validateContact(value);
      if (contactError) return contactError;

      const duplicateError = validateDuplicates(value, allValues, props, name);
      if (duplicateError) return duplicateError;

      return false;
    },
    [validateRequired, validateContact, validateDuplicates],
  );

  const handleAdd = useCallback(() => {
    if (fields.length < maxFields) {
      const newEntry = {
        ...defaultEntry,
        isPrimary: fields.length === 0,
      };
      fields.push(newEntry);
    }
  }, [fields, maxFields, defaultEntry]);

  const handleRemove = useCallback(
    (index) => {
      fields.remove(index);
      // After removal, ensure first item (if exists) is primary
      if (fields.length > 0 && index === 0) {
        const fieldArrayName = fields.name;
        change(`${fieldArrayName}[0].isPrimary`, true);
      }
    },
    [fields, change],
  );

  if (layout === 'horizontal') {
    // For Adult.js - inline layout with columns
    return (
      <>
        {fields.map((fieldName, index) => (
          <Row key={index}>
            <DynamicContactField
              fieldName={fieldName}
              index={index}
              type={type}
              typeOptions={localizedTypeOptions}
              onRemove={handleRemove}
              disabled={disabled}
              placeholder={getPlaceholder(index)}
              validate={validate}
              isPrimary={index === 0}
              canDelete={index === fields.length - 1 && fields.length > 1}
              showLabels={index === 0}
              label={
                index === 0
                  ? isEmail
                    ? intl.formatMessage(messages.email)
                    : intl.formatMessage(messages.phone)
                  : undefined
              }
              layout={layout}
              colSizes={colSizes}
            />
          </Row>
        ))}
        {fields.length < maxFields && !disabled && (
          <Row>
            <AddContactButton onClick={handleAdd} message={addMessage} />
          </Row>
        )}
      </>
    );
  }

  // Vertical layout (for future use)
  return (
    <div>
      {fields.map((fieldName, index) => (
        <DynamicContactField
          key={index}
          fieldName={fieldName}
          index={index}
          type={type}
          typeOptions={localizedTypeOptions}
          onRemove={handleRemove}
          disabled={disabled}
          placeholder={getPlaceholder(index)}
          validate={validate}
          isPrimary={index === 0}
          canDelete={index === fields.length - 1 && fields.length > 1}
          showLabels={index === 0}
          label={
            index === 0
              ? isEmail
                ? intl.formatMessage(messages.email)
                : intl.formatMessage(messages.phone)
              : undefined
          }
          layout={layout}
        />
      ))}
      {fields.length < maxFields && !disabled && (
        <div>
          <AddContactButton onClick={handleAdd} message={addMessage} />
        </div>
      )}
    </div>
  );
};

export default ContactFieldArray;
